import { resolveBrandclubHostDomain } from "../utils/domain.js";
const getDefaultApiBaseUrl = () => resolveBrandclubHostDomain();
const USE_HARDCODED_CONVERSATION = false;
const loadConversation = async ({
  conversationId,
  sharedConversationId,
  headersToInject,
  apiBaseUrl = getDefaultApiBaseUrl(),
  viewToken
}) => {
  if (!conversationId && !sharedConversationId) {
    return null;
  }
  try {
    const headers = {
      ...headersToInject,
      "Content-Type": "application/json"
    };
    const base = sharedConversationId ? `${apiBaseUrl}/api-rewards/concierge/conversation/shared/${sharedConversationId}/ui-messages` : `${apiBaseUrl}/api-rewards/concierge/conversation/${conversationId}/ui-messages`;
    const url = viewToken ? `${base}?viewToken=${encodeURIComponent(viewToken)}` : base;
    const response = await fetch(url, { method: "GET", headers });
    if (!response.ok) {
      if (response.status === 404) {
        return null;
      }
      if (response.status === 403) {
        const data = await response.json();
        return { messages: [], error: "unauthorized", redirect: data.redirect };
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const messagesData = await response.json();
    if (messagesData.error) {
      console.error("Error loading conversation:", messagesData.error);
      return null;
    }
    const messages = (messagesData.messages || []).map((message) => {
      var _a;
      const id = message.id || `msg-${message.messageIndex ?? Date.now()}`;
      const role = message.role === "user" ? "user" : "assistant";
      const rawParts = ((_a = message.content) == null ? void 0 : _a.parts) ?? message.parts;
      const parts = (rawParts == null ? void 0 : rawParts.length) ? rawParts : [{ type: "text", text: "" }];
      return {
        id,
        role,
        parts,
        ...message.createdAt ? { createdAt: message.createdAt } : {},
        ...message.messageIndex != null ? { messageIndex: message.messageIndex } : {},
        ...message.feedback ? { feedback: message.feedback } : {}
      };
    });
    return {
      conversationId: messagesData.conversationId || conversationId || void 0,
      status: messagesData.status,
      messages,
      followUpQuestions: messagesData.followUpQuestions || [],
      isSharedView: messagesData.isSharedView,
      sharedConversationId: messagesData.sharedConversationId || sharedConversationId || void 0,
      sharedAtMessageIndex: messagesData.sharedAtMessageIndex,
      sharedByUserName: messagesData.sharedByUserName,
      sourceConversationId: messagesData.sourceConversationId,
      sourceCheckpointIndex: messagesData.sourceCheckpointIndex
    };
  } catch (error) {
    console.error("Failed to load conversation:", error);
    return null;
  }
};
const sendMessageFeedback = async ({
  conversationId,
  messageIndex,
  feedbackType,
  headersToInject,
  apiBaseUrl = getDefaultApiBaseUrl()
}) => {
  const headers = {
    ...headersToInject,
    "Content-Type": "application/json"
  };
  const response = await fetch(`${apiBaseUrl}/api-rewards/concierge/message/feedback`, {
    method: "POST",
    headers,
    body: JSON.stringify({
      conversationId,
      messageIndex,
      feedbackType
    })
  });
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return response.json();
};
const setConversationSharing = async ({
  conversationId,
  enabled,
  messageIndex,
  headersToInject,
  apiBaseUrl = getDefaultApiBaseUrl()
}) => {
  const headers = {
    ...headersToInject,
    "Content-Type": "application/json"
  };
  const response = await fetch(`${apiBaseUrl}/api-rewards/concierge/conversation/${conversationId}/share`, {
    method: "POST",
    headers,
    body: JSON.stringify({ enabled, messageIndex })
  });
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return response.json();
};
const updateConversationTitle = async ({
  conversationId,
  title,
  headersToInject,
  apiBaseUrl = getDefaultApiBaseUrl()
}) => {
  const headers = {
    ...headersToInject,
    "Content-Type": "application/json"
  };
  const response = await fetch(`${apiBaseUrl}/api-rewards/concierge/conversations/${conversationId}`, {
    method: "PATCH",
    headers,
    body: JSON.stringify({ title: title.trim() })
  });
  if (!response.ok) {
    throw new Error(`Failed to update conversation title: ${response.status}`);
  }
};
const retryConversation = async ({
  conversationId,
  headersToInject,
  apiBaseUrl = getDefaultApiBaseUrl()
}) => {
  const headers = {
    ...headersToInject,
    "Content-Type": "application/json"
  };
  const response = await fetch(`${apiBaseUrl}/api-rewards/concierge/conversation/${conversationId}/retry`, {
    method: "POST",
    headers
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.error || `HTTP ${response.status}`);
  }
  return response.json();
};
const endLiveAgent = async ({
  conversationId,
  headersToInject,
  apiBaseUrl = getDefaultApiBaseUrl()
}) => {
  const headers = {
    ...headersToInject,
    "Content-Type": "application/json"
  };
  const response = await fetch(
    `${apiBaseUrl}/api-rewards/concierge/support/conversation/${conversationId}/end-live-agent`,
    {
      method: "POST",
      headers
    }
  );
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.error || `HTTP ${response.status}`);
  }
  return response.json();
};
const stopConversation = async ({
  conversationId,
  headersToInject,
  apiBaseUrl = getDefaultApiBaseUrl()
}) => {
  const headers = {
    ...headersToInject,
    "Content-Type": "application/json"
  };
  const response = await fetch(`${apiBaseUrl}/api-rewards/concierge/conversation/${conversationId}/stop`, {
    method: "POST",
    headers
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.error || `HTTP ${response.status}`);
  }
  return response.json();
};
const fetchDevOptions = async ({
  headersToInject,
  apiBaseUrl = getDefaultApiBaseUrl()
} = {}) => {
  try {
    const headers = {
      ...headersToInject,
      "Content-Type": "application/json"
    };
    const response = await fetch(`${apiBaseUrl}/api-rewards/concierge/devOptions`, {
      method: "GET",
      headers
    });
    if (!response.ok) {
      console.warn("Failed to fetch dev options from server, using local defaults");
      return null;
    }
    return await response.json();
  } catch (error) {
    console.warn("Error fetching dev options:", error);
    return null;
  }
};
const fetchProductByStacklineSku = async ({
  stacklineSku,
  headersToInject,
  apiBaseUrl = getDefaultApiBaseUrl()
}) => {
  try {
    const headers = {
      ...headersToInject,
      "Content-Type": "application/json"
    };
    const response = await fetch(`${apiBaseUrl}/api-rewards/product/${stacklineSku}`, {
      method: "GET",
      headers
    });
    if (!response.ok) {
      if (response.status === 404) {
        return null;
      }
      throw new Error(`Failed to fetch product: ${response.status}`);
    }
    const data = await response.json();
    return data.product || data;
  } catch (error) {
    console.error("Error fetching product by stacklineSku:", error);
    return null;
  }
};
const fetchProductWithVariants = async ({
  stacklineSku,
  headersToInject,
  apiBaseUrl = getDefaultApiBaseUrl()
}) => {
  try {
    const headers = {
      ...headersToInject,
      "Content-Type": "application/json"
    };
    const response = await fetch(`${apiBaseUrl}/api-rewards/product/${stacklineSku}/with-variants`, {
      method: "GET",
      headers
    });
    if (!response.ok) {
      if (response.status === 404) {
        return null;
      }
      throw new Error(`Failed to fetch product with variants: ${response.status}`);
    }
    const data = await response.json();
    return data.product || data;
  } catch (error) {
    console.error("Error fetching product with variants:", error);
    return null;
  }
};
const fetchFollowUpSuggestions = async (conversationId, {
  headersToInject,
  apiBaseUrl = getDefaultApiBaseUrl(),
  devOptions
} = {}) => {
  try {
    const response = await fetch(`${apiBaseUrl}/api-rewards/concierge/conversation/${conversationId}/follow-ups`, {
      method: "POST",
      headers: { ...headersToInject, "Content-Type": "application/json" },
      body: JSON.stringify({ devOptions })
    });
    if (!response.ok) return [];
    const data = await response.json();
    return Array.isArray(data.questions) ? data.questions : [];
  } catch {
    return [];
  }
};
const mintVoiceSession = async ({
  conversationId,
  pdpStacklineSku,
  headersToInject,
  apiBaseUrl = getDefaultApiBaseUrl()
}) => {
  const response = await fetch(`${apiBaseUrl}/api-rewards/concierge/voice/session`, {
    method: "POST",
    headers: { ...headersToInject, "Content-Type": "application/json" },
    body: JSON.stringify({ conversationId, pdpStacklineSku })
  });
  if (!response.ok) {
    const body = await response.text().catch(() => "");
    throw new Error(`mintVoiceSession failed (${response.status}): ${body.slice(0, 200)}`);
  }
  return await response.json();
};
const DEFAULT_PRIMING_TEXT_LIMIT = 20;
const DEFAULT_PRIMING_TOOL_LIMIT = 3;
const MAX_TOOL_OUTPUT_CHARS = 2e3;
const loadPriorThreadMessages = async ({
  conversationId,
  limit = DEFAULT_PRIMING_TEXT_LIMIT,
  toolLimit = DEFAULT_PRIMING_TOOL_LIMIT,
  headersToInject,
  apiBaseUrl = getDefaultApiBaseUrl()
}) => {
  var _a, _b;
  if (!conversationId) return [];
  try {
    const headers = {
      ...headersToInject,
      "Content-Type": "application/json"
    };
    const url = `${apiBaseUrl}/api-rewards/concierge/conversation/${conversationId}/ui-messages?includeToolParts=true`;
    const response = await fetch(url, { method: "GET", headers });
    if (!response.ok) return [];
    let messagesData;
    try {
      messagesData = await response.json();
    } catch {
      return [];
    }
    if (messagesData.error) return [];
    const allMessages = messagesData.messages ?? [];
    const safeLimit = Math.max(0, Math.floor(limit));
    const windowed = safeLimit > 0 ? allMessages.slice(-safeLimit) : allMessages;
    const items = [];
    const pendingToolCalls = /* @__PURE__ */ new Map();
    for (const message of windowed) {
      const role = message.role === "user" ? "user" : "assistant";
      const rawParts = ((_a = message.content) == null ? void 0 : _a.parts) ?? message.parts ?? [];
      const text = rawParts.filter((p) => p && p.type === "text" && typeof p.text === "string").map((p) => p.text).join("\n\n");
      if (text) items.push({ kind: "text", role, text });
      for (const part of rawParts) {
        if (!(part == null ? void 0 : part.type)) continue;
        if (part.type === "tool-call" && typeof part.toolCallId === "string") {
          const callId = part.toolCallId;
          const toolName = typeof part.toolName === "string" ? part.toolName : "";
          let argsJson = "{}";
          try {
            argsJson = JSON.stringify(part.args ?? {});
          } catch {
            argsJson = "{}";
          }
          if (callId && toolName) {
            pendingToolCalls.set(callId, { toolName, argsJson });
          }
        } else if (part.type === "tool-result" && typeof part.toolCallId === "string") {
          const callId = part.toolCallId;
          const pending = pendingToolCalls.get(callId);
          if (pending) {
            const narrator = (_b = part == null ? void 0 : part.result) == null ? void 0 : _b.narrator;
            items.push({
              kind: "tool",
              callId,
              toolName: pending.toolName,
              argsJson: pending.argsJson,
              output: typeof narrator === "string" ? narrator : ""
            });
            pendingToolCalls.delete(callId);
          }
        } else if (part.type === "tool-invocation" && part.toolInvocation) {
          const ti = part.toolInvocation;
          if (ti.state === "result" && typeof ti.toolCallId === "string" && typeof ti.toolName === "string") {
            let argsJson = "{}";
            try {
              argsJson = JSON.stringify(ti.args ?? {});
            } catch {
              argsJson = "{}";
            }
            let output = "";
            try {
              output = JSON.stringify(ti.result ?? {});
              if (output.length > MAX_TOOL_OUTPUT_CHARS) {
                output = output.slice(0, MAX_TOOL_OUTPUT_CHARS) + "…[truncated]";
              }
            } catch {
              output = "";
            }
            items.push({
              kind: "tool",
              callId: ti.toolCallId,
              toolName: ti.toolName,
              argsJson,
              output
            });
          }
        }
      }
    }
    const safeToolLimit = Math.max(0, Math.floor(toolLimit));
    const toolCount = items.reduce((n, it) => it.kind === "tool" ? n + 1 : n, 0);
    const toolsToDrop = Math.max(0, toolCount - safeToolLimit);
    if (toolsToDrop === 0) return items;
    let droppedSoFar = 0;
    return items.filter((it) => {
      if (it.kind !== "tool") return true;
      if (droppedSoFar < toolsToDrop) {
        droppedSoFar++;
        return false;
      }
      return true;
    });
  } catch {
    return [];
  }
};
const postVoiceEvent = async ({
  conversationId,
  events,
  headersToInject,
  apiBaseUrl = getDefaultApiBaseUrl()
}) => {
  try {
    await fetch(`${apiBaseUrl}/api-rewards/concierge/voice/event`, {
      method: "POST",
      headers: { ...headersToInject, "Content-Type": "application/json" },
      body: JSON.stringify({ conversationId, events }),
      keepalive: true
    });
  } catch {
  }
};
const proxyVoiceTool = async ({
  conversationId,
  callId,
  toolName,
  args,
  headersToInject,
  apiBaseUrl = getDefaultApiBaseUrl()
}) => {
  const response = await fetch(`${apiBaseUrl}/api-rewards/concierge/voice/tool`, {
    method: "POST",
    headers: { ...headersToInject, "Content-Type": "application/json" },
    body: JSON.stringify({ conversationId, callId, toolName, args })
  });
  if (!response.ok) {
    return {
      status: "error",
      narratorText: "Sorry, I had a problem with that. Want to try again?",
      toolResultId: `${callId}-fail-${Date.now()}`
    };
  }
  return await response.json();
};
const endVoiceCall = async ({
  conversationId,
  reason,
  headersToInject,
  apiBaseUrl = getDefaultApiBaseUrl()
}) => {
  try {
    const response = await fetch(`${apiBaseUrl}/api-rewards/concierge/voice/end`, {
      method: "POST",
      headers: { ...headersToInject, "Content-Type": "application/json" },
      body: JSON.stringify({ conversationId, reason }),
      keepalive: true
    });
    if (!response.ok) return null;
    return await response.json();
  } catch {
    return null;
  }
};
const postVoiceAuthPromote = async ({
  conversationId,
  durationMs,
  blockedToolName,
  headersToInject,
  apiBaseUrl = getDefaultApiBaseUrl()
}) => {
  try {
    const response = await fetch(`${apiBaseUrl}/api-rewards/concierge/voice/auth-promote`, {
      method: "POST",
      headers: { ...headersToInject, "Content-Type": "application/json" },
      body: JSON.stringify({ conversationId, durationMs, blockedToolName })
    });
    if (!response.ok) return { ok: false };
    return await response.json();
  } catch {
    return { ok: false };
  }
};
const postVoiceDebugWidget = async ({
  conversationId,
  type,
  data,
  status,
  headersToInject,
  apiBaseUrl = getDefaultApiBaseUrl()
}) => {
  const response = await fetch(`${apiBaseUrl}/api-rewards/concierge/voice/debug/widget`, {
    method: "POST",
    headers: { ...headersToInject, "Content-Type": "application/json" },
    body: JSON.stringify({ conversationId, type, data, status })
  });
  if (!response.ok) {
    const body = await response.text().catch(() => "");
    throw new Error(`postVoiceDebugWidget failed (${response.status}): ${body.slice(0, 200)}`);
  }
  return response.json();
};
const streamVoiceEventChannel = ({
  conversationId,
  accessToken,
  visitorId,
  apiBaseUrl = getDefaultApiBaseUrl()
}) => {
  const base = `${apiBaseUrl}/api-rewards/concierge/voice/event-channel/${encodeURIComponent(conversationId)}`;
  const params = new URLSearchParams();
  if (accessToken) params.set("token", accessToken);
  if (visitorId) params.set("visitorId", visitorId);
  const qs = params.toString();
  const url = qs ? `${base}?${qs}` : base;
  return new EventSource(url, { withCredentials: true });
};
const initVoiceRecording = async ({
  conversationId,
  mimeUser,
  mimeAssistant,
  extUser,
  extAssistant,
  headersToInject,
  apiBaseUrl = getDefaultApiBaseUrl()
}) => {
  try {
    const response = await fetch(`${apiBaseUrl}/api-rewards/concierge/voice/recording/init`, {
      method: "POST",
      headers: { ...headersToInject, "Content-Type": "application/json" },
      body: JSON.stringify({ conversationId, mimeUser, mimeAssistant, extUser, extAssistant })
    });
    if (!response.ok) return { enabled: false, reason: `http_${response.status}` };
    return await response.json();
  } catch {
    return { enabled: false, reason: "network_error" };
  }
};
const finalizeVoiceRecording = async ({
  conversationId,
  track,
  durationMs,
  mimeType,
  byteSize,
  uploadOk,
  headersToInject,
  apiBaseUrl = getDefaultApiBaseUrl()
}) => {
  try {
    const response = await fetch(`${apiBaseUrl}/api-rewards/concierge/voice/recording/finalize`, {
      method: "POST",
      headers: { ...headersToInject, "Content-Type": "application/json" },
      body: JSON.stringify({ conversationId, track, durationMs, mimeType, byteSize, uploadOk }),
      keepalive: true
    });
    if (!response.ok) return { ok: false, error: `http_${response.status}` };
    return await response.json();
  } catch {
    return { ok: false, error: "network_error" };
  }
};
const getVoicePublicSummary = async ({
  conversationId,
  headersToInject,
  apiBaseUrl = getDefaultApiBaseUrl()
}) => {
  try {
    const response = await fetch(
      `${apiBaseUrl}/api-rewards/concierge/voice/sessions/${encodeURIComponent(conversationId)}/public-summary`,
      { headers: { ...headersToInject }, credentials: "include" }
    );
    if (response.status === 404) return null;
    if (!response.ok) return null;
    return await response.json();
  } catch {
    return null;
  }
};
const voiceTranscriptMarkdownUrl = ({
  conversationId,
  apiBaseUrl = getDefaultApiBaseUrl()
}) => `${apiBaseUrl}/api-rewards/concierge/voice/sessions/${encodeURIComponent(conversationId)}/transcript.md`;
export {
  USE_HARDCODED_CONVERSATION,
  endLiveAgent,
  endVoiceCall,
  fetchDevOptions,
  fetchFollowUpSuggestions,
  fetchProductByStacklineSku,
  fetchProductWithVariants,
  finalizeVoiceRecording,
  getVoicePublicSummary,
  initVoiceRecording,
  loadConversation,
  loadPriorThreadMessages,
  mintVoiceSession,
  postVoiceAuthPromote,
  postVoiceDebugWidget,
  postVoiceEvent,
  proxyVoiceTool,
  retryConversation,
  sendMessageFeedback,
  setConversationSharing,
  stopConversation,
  streamVoiceEventChannel,
  updateConversationTitle,
  voiceTranscriptMarkdownUrl
};
//# sourceMappingURL=api.js.map
